const OFFER_SELECTOR = '[data-kingrax-email-offer]';
const OFFER_HASH = '#kingrax-email-offer';
const DISMISSED_KEY = 'kingrax-email-offer:dismissed-at:v2';
const SUBSCRIBED_KEY = 'kingrax-email-offer:subscribed-at:v2';

function readStoredTime(key) {
  try {
    const value = Number.parseInt(window.localStorage.getItem(key) || '', 10);
    return Number.isFinite(value) ? value : 0;
  } catch {
    return 0;
  }
}

function storeTime(key) {
  try {
    window.localStorage.setItem(key, String(Date.now()));
  } catch {
    // Storage can be unavailable in private browsing; the offer still works.
  }
}

function isFresh(timestamp, days) {
  return timestamp > 0 && Date.now() - timestamp < days * 24 * 60 * 60 * 1000;
}

function initializeEmailOffer() {
  const dialog = document.querySelector(OFFER_SELECTOR);
  if (!(dialog instanceof HTMLElement)) return;

  const closeButtons = dialog.querySelectorAll('[data-kingrax-offer-close]');
  const closeButton = dialog.querySelector('[data-kingrax-offer-close]');
  const fallbackBackdrop = document.querySelector('[data-kingrax-offer-fallback-backdrop]');
  const form = dialog.querySelector('.kingrax-email-offer__form');
  const submitButton = dialog.querySelector('[data-kingrax-offer-submit]');
  const copyButton = dialog.querySelector('[data-kingrax-offer-copy]');
  const codeElement = dialog.querySelector('[data-kingrax-offer-code]');
  const liveRegion = dialog.querySelector('[data-kingrax-offer-live]');
  const hasSuccess = dialog.querySelector('[data-kingrax-offer-success]') !== null;
  const hasError = dialog.querySelector('[data-kingrax-offer-error]') !== null;
  const forceOfferPreview = new URL(window.location.href).searchParams.get('show_email_offer') === '1';
  const supportsModalDialog = typeof dialog.showModal === 'function' && typeof dialog.close === 'function';
  let lastTrigger = null;
  let autoOpenTimer = null;
  let fallbackBackgroundState = [];

  function isDialogOpen() {
    return dialog.hasAttribute('open');
  }

  function restoreTriggerFocus() {
    if (lastTrigger instanceof HTMLElement && document.contains(lastTrigger)) {
      lastTrigger.focus({ preventScroll: true });
    }
  }

  function isolateFallbackBackground() {
    fallbackBackgroundState = Array.from(document.body.children)
      .filter((element) => element !== dialog && element !== fallbackBackdrop)
      .map((element) => ({
        element,
        hadInert: element.hasAttribute('inert'),
        ariaHidden: element.getAttribute('aria-hidden'),
      }));

    fallbackBackgroundState.forEach(({ element }) => {
      element.setAttribute('inert', '');
      element.setAttribute('aria-hidden', 'true');
    });
  }

  function restoreFallbackBackground() {
    fallbackBackgroundState.forEach(({ element, hadInert, ariaHidden }) => {
      if (!hadInert) element.removeAttribute('inert');
      if (ariaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', ariaHidden);
      }
    });
    fallbackBackgroundState = [];
  }

  function openDialog(trigger = null) {
    if (isDialogOpen()) return;
    lastTrigger = trigger instanceof HTMLElement ? trigger : document.activeElement;

    if (supportsModalDialog) {
      dialog.showModal();
    } else {
      dialog.classList.add('kingrax-email-offer--fallback');
      dialog.setAttribute('open', '');
      dialog.setAttribute('aria-modal', 'true');
      document.body.classList.add('kingrax-email-offer-is-open');
      if (fallbackBackdrop instanceof HTMLElement) fallbackBackdrop.hidden = false;
      if (closeButton instanceof HTMLButtonElement) closeButton.focus({ preventScroll: true });
      isolateFallbackBackground();
    }

    window.requestAnimationFrame(() => {
      if (closeButton instanceof HTMLButtonElement) closeButton.focus({ preventScroll: true });
    });
  }

  function closeDialog(rememberDismissal = true) {
    if (!isDialogOpen()) return;
    if (rememberDismissal && !hasSuccess) storeTime(DISMISSED_KEY);

    if (supportsModalDialog) {
      dialog.close();
    } else {
      dialog.removeAttribute('open');
      dialog.removeAttribute('aria-modal');
      dialog.classList.remove('kingrax-email-offer--fallback');
      document.body.classList.remove('kingrax-email-offer-is-open');
      if (fallbackBackdrop instanceof HTMLElement) fallbackBackdrop.hidden = true;
      restoreFallbackBackground();
      restoreTriggerFocus();
    }
  }

  document.querySelectorAll(`a[href$="${OFFER_HASH}"], [data-kingrax-offer-trigger]`).forEach((trigger) => {
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      window.clearTimeout(autoOpenTimer);
      openDialog(trigger);
    });
  });

  closeButtons.forEach((button) => {
    button.addEventListener('click', () => closeDialog(true));
  });

  if (fallbackBackdrop instanceof HTMLElement) {
    fallbackBackdrop.addEventListener('click', () => closeDialog(true));
  }

  dialog.addEventListener('cancel', () => {
    if (!hasSuccess) storeTime(DISMISSED_KEY);
  });

  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) closeDialog(true);
  });

  dialog.addEventListener('close', () => {
    restoreTriggerFocus();
  });

  document.addEventListener('keydown', (event) => {
    if (!isDialogOpen()) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDialog(true);
      return;
    }

    if (supportsModalDialog || event.key !== 'Tab') return;
    const focusable = Array.from(
      dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element instanceof HTMLElement && element.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('focusin', (event) => {
    if (supportsModalDialog || !isDialogOpen() || dialog.contains(event.target)) return;
    if (closeButton instanceof HTMLButtonElement) closeButton.focus({ preventScroll: true });
  });

  if (form instanceof HTMLFormElement) {
    form.addEventListener('submit', () => {
      if (!(submitButton instanceof HTMLButtonElement)) return;
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
      submitButton.textContent = 'Getting your code…';
    });
  }

  if (copyButton instanceof HTMLButtonElement && codeElement) {
    copyButton.addEventListener('click', async () => {
      const code = codeElement.textContent?.trim();
      if (!code) return;
      let copied = false;

      try {
        await navigator.clipboard.writeText(code);
        copied = true;
      } catch {
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(codeElement);
        selection?.removeAllRanges();
        selection?.addRange(range);
        copied = document.execCommand('copy');
        if (copied) selection?.removeAllRanges();
      }

      if (copied) {
        copyButton.textContent = 'Copied';
        if (liveRegion) liveRegion.textContent = 'Discount code copied.';
      } else {
        copyButton.textContent = 'Copy manually';
        if (liveRegion) liveRegion.textContent = 'Copy failed. The code is selected for manual copying.';
      }
    });
  }

  if (hasSuccess) {
    storeTime(SUBSCRIBED_KEY);
    window.requestAnimationFrame(() => openDialog());
    return;
  }

  if (hasError) {
    window.requestAnimationFrame(() => openDialog());
    return;
  }

  const dismissDays = Number.parseInt(dialog.dataset.dismissDays || '7', 10);
  const successDays = Number.parseInt(dialog.dataset.successDays || '180', 10);
  const wasDismissed = isFresh(readStoredTime(DISMISSED_KEY), dismissDays);
  const hasSubscribed = isFresh(readStoredTime(SUBSCRIBED_KEY), successDays);
  const shouldAutoOpen = dialog.dataset.autoOpen === 'true' && !window.Shopify?.designMode;

  if (forceOfferPreview || (shouldAutoOpen && !wasDismissed && !hasSubscribed)) {
    const delay = forceOfferPreview ? 500 : Number.parseInt(dialog.dataset.autoOpenDelay || '3000', 10);
    autoOpenTimer = window.setTimeout(() => openDialog(), delay);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeEmailOffer, { once: true });
} else {
  initializeEmailOffer();
}
